import clsx from "clsx";
import { ComponentPropsWithoutRef, ReactElement } from "react";

import { SidebarItem } from "./catalyst/SidebarItem";
import { SidebarLabel } from "./catalyst/SidebarLabel";
import { useSidebarBehavior } from "./collapsible-sidebar-behavior";

type CollapsibleSidebarItemProps = {
  button?: React.ReactNode;
  label?: string;
} & ComponentPropsWithoutRef<typeof SidebarItem>;

export const CollapsibleSidebarItem = ({
  button,
  children,
  className,
  label,
  title,
  ...props
}: CollapsibleSidebarItemProps): ReactElement => {
  const { collapsed, itemTitle } = useSidebarBehavior();

  // If caller supplies `title`, keep it. Otherwise derive from `label`.
  const computedTitle = title ?? (label ? itemTitle(label) : undefined);

  if (!button || collapsed) {
    return (
      <SidebarItem {...props} className={className} title={computedTitle}>
        {children}
      </SidebarItem>
    );
  }

  return (
    <div className="flex w-full items-center gap-0.5">
      <SidebarItem
        {...props}
        className={clsx("min-w-0 flex-1", className)}
        title={computedTitle}
      >
        {children}
      </SidebarItem>
      <div className="shrink-0">{button}</div>
    </div>
  );
};

type CollapsibleSidebarLabelProps = ComponentPropsWithoutRef<
  typeof SidebarLabel
>;

export const CollapsibleSidebarLabel = (
  props: CollapsibleSidebarLabelProps,
): ReactElement => {
  const { labelClassName } = useSidebarBehavior();
  return (
    <SidebarLabel
      {...props}
      className={clsx(labelClassName, props.className)}
    />
  );
};
